import type { PlanImageCard } from './types'

const PDF_POINTS_PER_CM = 72 / 2.54

const labelLineHeight = (fontSize: number): number => fontSize * 1.2

const titleBandHeightPdfPoints = (fontSize: number, titleLineCount: number, padding = 2): number =>
  titleLineCount * labelLineHeight(fontSize) + 2 * padding

const pdfPointsToWorldMeters = (pdfPoints: number, planScale: number): number =>
  (pdfPoints / PDF_POINTS_PER_CM / 100) * planScale

const pdfCmToWorldMeters = (pdfCm: number, planScale: number): number => (pdfCm / 100) * planScale

export interface PlanImageCardLayoutDimensions {
  width: number
  totalHeight: number
  titleBandHeight: number
  imageHeight: number
}

interface ComputeLayoutOptions {
  planScale: number
  imageAspectRatio?: number | null
}

/**
 * Mirrors server-side image card layout (`makeImageCard`): top-left anchor, card extends downward
 * in world space (decreasing Y).
 */
export const computePlanImageCardLayoutDimensions = (
  card: Pick<PlanImageCard, 'width' | 'titleLines' | 'textStyle'>,
  { planScale, imageAspectRatio = 1 }: ComputeLayoutOptions,
): PlanImageCardLayoutDimensions => {
  const titleFontSize = card.textStyle.fontSize ?? 8
  const titleLineCount = Math.max(card.titleLines.length, 1)
  const contentInsetPdfPoints = 2
  const titleGapPdfPoints = 1

  const width = pdfCmToWorldMeters(card.width, planScale)
  const innerImageWidthPdfPoints = card.width * PDF_POINTS_PER_CM - 2 * contentInsetPdfPoints
  const safeAspectRatio = imageAspectRatio && imageAspectRatio > 0 ? imageAspectRatio : 1
  const imageHeightPdfPoints = innerImageWidthPdfPoints / safeAspectRatio
  const titleBandHeightPdf = titleBandHeightPdfPoints(titleFontSize, titleLineCount)
  const totalHeightPdfPoints =
    contentInsetPdfPoints +
    titleBandHeightPdf +
    titleGapPdfPoints +
    imageHeightPdfPoints +
    contentInsetPdfPoints

  return {
    width,
    totalHeight: pdfPointsToWorldMeters(totalHeightPdfPoints, planScale),
    titleBandHeight: pdfPointsToWorldMeters(titleBandHeightPdf, planScale),
    imageHeight: pdfPointsToWorldMeters(imageHeightPdfPoints, planScale),
  }
}
